import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PasswordField } from '../components/PasswordField';
import { LoadingScreen } from '../components/LoadingScreen';
import { AuthAPI } from '../services/api';
import { AlertService } from '../services/AlertService';
import { resetPasswordSchema, ResetPasswordFormData } from '../schemas/validation';
import '../components/AuthForms.css';

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState<string>('');
  const [validToken, setValidToken] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const alertService = AlertService;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  useEffect(() => {
    const resetToken = searchParams.get('token');
    if (!resetToken) {
      setError('Invalid reset link. Please check your email or request a new password reset.');
      setValidToken(false);
      return;
    }
    setToken(resetToken);
    setValidToken(true);
  }, [searchParams]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      setLoading(true);
      setError('');

      await AuthAPI.resetPassword(token, data.newPassword);
      
      alertService.success('Password reset successfully! You can now log in with your new password.');
      navigate('/login');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (validToken === false) {
    return (
      <>
        <div className="auth-page">
          <div className="auth-form-container">
            <div className="auth-form">
              <h2>Invalid Reset Link</h2>
              <div className="error-message">
                {error || 'This password reset link is invalid or has expired. Please request a new password reset.'}
              </div>
              <div className="form-buttons">
                <button 
                  type="button"
                  onClick={() => navigate('/login')}
                  className="btn btn-primary"
                >
                  Back to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (validToken === null) {
    return (
      <>
        <div className="auth-page">
          <div className="auth-form-container">
            <div className="auth-form">
              <h2>Loading...</h2>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="auth-page">
        <div className="auth-form-container">
          <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
            <h2>Reset Your Password</h2>
            
            <p className="info-text">
              Please enter your new password. Make sure it's strong and secure.
            </p>

            {error && <div className="error-message">{error}</div>}

            <PasswordField
              id="newPassword"
              label="New Password"
              placeholder="Enter your new password"
              register={register}
              error={errors.newPassword?.message}
              disabled={loading}
              showRequirements={true}
            />

            <PasswordField
              id="confirmPassword"
              label="Confirm New Password"
              placeholder="Confirm your new password"
              register={register}
              error={errors.confirmPassword?.message}
              disabled={loading}
            />

            <div className="form-buttons">
              <button 
                type="submit" 
                disabled={loading}
                className="btn btn-primary submit-button"
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </div>

            <div className="switch-form">
              <button 
                type="button"
                onClick={() => navigate('/login')}
                className="btn btn-ghost"
                disabled={loading}
              >
                Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Loading Screen Overlay */}
      <LoadingScreen isVisible={loading} message="Resetting your password..." />
    </>
  );
};